import { z } from "zod";

/**
 * Shared DTO cho Admin User Lifecycle — suspend / restore (US-AE3.F3-0011 / @UC9022).
 * Một nguồn sự thật cho api (S01 — endpoint) và admin web (S03 — dialog lý do + action),
 * tránh drift shape ở 2 nơi (DRY).
 *
 * BẢO MẬT (invariants):
 * - Request `.strict()` — chỉ nhận `reason`, chặn field lạ (chống lẫn PII/secret vào body).
 * - Response chỉ định danh + status + count — KHÔNG PII/secret/tiền.
 * - Lý do (reason) lưu vào audit_log ở backend (KHÔNG cột riêng, theo human-decision F3 §6).
 */

/** Độ dài tối đa lý do suspend — chặn input phình; đủ cho ghi chú nghiệp vụ. */
export const SUSPEND_REASON_MAX_LENGTH = 500;

/** Body POST /admin/users/:id/suspend — reason bắt buộc (1..500). `.strict()` chặn field lạ. */
export const suspendUserRequestSchema = z
  .object({
    reason: z.string().min(1).max(SUSPEND_REASON_MAX_LENGTH),
  })
  .strict();

export type SuspendUserRequest = z.infer<typeof suspendUserRequestSchema>;

/** Response suspend: status='suspended' + số phiên đã revoke (đối xứng deactivateAdminMember). */
export const suspendUserResponseSchema = z.object({
  userId: z.string(),
  status: z.literal("suspended"),
  revokedSessionCount: z.number().int().min(0),
});

export type SuspendUserResponse = z.infer<typeof suspendUserResponseSchema>;

/** Response restore: status='active' (KHÔNG tự khôi phục phiên — user phải login lại). */
export const restoreUserResponseSchema = z.object({
  userId: z.string(),
  status: z.literal("active"),
});

export type RestoreUserResponse = z.infer<typeof restoreUserResponseSchema>;

/**
 * Shared DTO cho Admin Edit-Profile — sửa hồ sơ user (US-AE-IMP2-T11-S3 / @UC9153).
 *
 * PO CHỐT 07/07: build PATCH /admin/users/:id sửa fullName/email/phone; RBAC SUPER_ADMIN/OPS;
 * audit; validate biên. Endpoint MUTATE PII → guardrails chặt:
 * - Request `.strict()` — CHỈ nhận fullName/email/phone (mọi field partial-optional), chặn field lạ
 *   (chống lẫn status/passwordHash/secret vào body — KHÔNG cho sửa status qua edit-profile).
 * - MIN 1 field: ít nhất 1 trong 3 field phải có (refine) — chặn PATCH rỗng no-op.
 * - email: định dạng email hợp lệ hoặc null (gỡ email). phone: E.164 (`+` + 8..15 chữ số) hoặc null.
 *   fullName: 1..200 hoặc null (gỡ tên). Trim + validate BIÊN (C5) trước khi vào service.
 * - Response chỉ định danh + field đã cập nhật (redact theo vai ở backend) — KHÔNG secret/tiền.
 * - Uniqueness email/phone enforce ở service (DB unique) → 409 nếu trùng (KHÔNG ở contract).
 */

/** Độ dài tối đa fullName khi sửa hồ sơ — chặn input phình. */
export const EDIT_PROFILE_FULLNAME_MAX_LENGTH = 200;

/** E.164: dấu `+` + 8..15 chữ số (đối xứng phoneE164 toàn hệ). null = gỡ số. */
const editPhoneSchema = z
  .string()
  .regex(/^\+\d{8,15}$/, "Số điện thoại phải định dạng E.164 (ví dụ +84901234567)")
  .nullable();

/** email hợp lệ hoặc null (gỡ email). Chuẩn hoá lowercase ở service (KHÔNG ở contract). */
const editEmailSchema = z.string().email("Email không hợp lệ").nullable();

/** fullName 1..200 hoặc null (gỡ tên). */
const editFullNameSchema = z.string().min(1).max(EDIT_PROFILE_FULLNAME_MAX_LENGTH).nullable();

/**
 * Body PATCH /admin/users/:id (@UC9153) — sửa hồ sơ. `.strict()` chặn field lạ (KHÔNG status/secret).
 * Mỗi field OPTIONAL (partial update); refine ≥1 field để chặn body rỗng no-op (400).
 */
export const editUserProfileRequestSchema = z
  .object({
    fullName: editFullNameSchema.optional(),
    email: editEmailSchema.optional(),
    phone: editPhoneSchema.optional(),
  })
  .strict()
  .refine(
    (b) => b.fullName !== undefined || b.email !== undefined || b.phone !== undefined,
    { message: "Phải cung cấp ít nhất một trường để cập nhật" },
  );

export type EditUserProfileRequest = z.infer<typeof editUserProfileRequestSchema>;

/**
 * Response edit-profile: định danh + field hiện tại (redact theo vai ở backend). status giữ nguyên
 * (edit-profile KHÔNG đổi status). PII nullable/optional (redaction MASK / anonymized → null).
 */
export const editUserProfileResponseSchema = z
  .object({
    userId: z.string(),
    status: z.enum(["active", "suspended", "anonymized"]),
    fullName: z.string().nullable().optional(),
    email: z.string().nullable().optional(),
    phone: z.string().nullable().optional(),
  })
  .strict();

export type EditUserProfileResponse = z.infer<typeof editUserProfileResponseSchema>;

/** Code 409 khi email/phone mới trùng user khác (DB unique) — UI hiện lý do rõ. */
export const EDIT_PROFILE_DUPLICATE_CODE = "PROFILE_FIELD_DUPLICATE";

/** Code 409 khi sửa hồ sơ user đã anonymized (trạng thái cuối — không cho tái định danh). */
export const EDIT_PROFILE_ANONYMIZED_CODE = "USER_ANONYMIZED";

/**
 * 🔴🔴 US-AE16.F6-0374-S03 (@UC9649) — REVOKE PHIÊN **TÁCH KHỎI** SUSPEND.
 *
 * VÌ SAO: revoke phiên end-user trước đây **chỉ tồn tại bên trong `suspend()`**. Muốn đá một tài
 * khoản nghi bị chiếm ra khỏi mọi thiết bị thì phải **suspend cả tài khoản** — đòn quá nặng: người
 * dùng mất luôn quyền đăng nhập lại, trong khi việc cần làm chỉ là cắt phiên đang sống.
 *
 * ⚠️ ĐỪNG NHẦM với `POST /admin/members/{id}/sessions/revoke` — đó là phiên **ADMIN HỆ THỐNG**
 * (`tx.adminSession`), bảng khác, actor khác.
 *
 * 🔴 GIỚI HẠN PHẢI NÓI THẬT TRÊN UI: revoke chỉ chặn **refresh token**. **Access JWT hiện hành
 * (TTL ~15 phút) VẪN SỐNG** tới khi hết hạn. Nhãn hứa *"đăng xuất mọi thiết bị NGAY LẬP TỨC"* là
 * nói dối về một tính năng bảo mật — loại nói dối nguy hiểm nhất, vì người dùng sẽ ngừng làm các
 * bước phòng vệ khác (đổi mật khẩu, báo ngân hàng).
 */
export const REVOKE_SESSIONS_REASON_MAX_LENGTH = 500;

/** Body POST /admin/users/:id/sessions/revoke — `reason` bắt buộc. `.strict()` chặn field lạ. */
export const revokeUserSessionsRequestSchema = z
  .object({
    reason: z.string().trim().min(1).max(REVOKE_SESSIONS_REASON_MAX_LENGTH),
  })
  .strict();

export type RevokeUserSessionsRequest = z.infer<typeof revokeUserSessionsRequestSchema>;

/**
 * Response revoke. 🔴 **KHÔNG có trường `status`** — cố ý: endpoint này **không đổi `user.status`**,
 * và đó là toàn bộ lý do nó tồn tại. Trả `status` sẽ mời gọi người đọc tưởng nó có đổi.
 */
export const revokeUserSessionsResponseSchema = z
  .object({
    userId: z.string(),
    revokedCount: z.number().int().min(0),
  })
  .strict();

export type RevokeUserSessionsResponse = z.infer<typeof revokeUserSessionsResponseSchema>;
